/* Built by Alpas Made. */
/*
 * QUICK POPUP FOR THE DESIGN GRID
 *
 * A plain click on a design's picture or title in the collection grid, the
 * in-collection search results or the /search page opens the design in the
 * theme's own quick popup instead of leaving the page. Back, a swipe, Escape,
 * a tap on the dimmed page and the close button each close only the popup, and
 * the shopper is still on the grid where they were.
 *
 * Rendered by snippets/kc-quick-view-shell.liquid, which main-collection and
 * main-search include while their "Open designs in a quick popup" setting is on.
 * The popup's content is the design's ?view=modal page
 * (sections/main-product-modal.liquid), with the product page's own purchase
 * area in it.
 *
 * HOW IT FITS THE THEME
 * theme.js is not edited. This file defines <kc-quick-view> as a subclass of
 * the theme's <quick-view> (class QuickView in assets/theme.js), and changes
 * only what a single shared popup needs: a request that can fail and be
 * retried, named load states with a loading view and an error view, a history
 * entry so Back closes the popup, a close that must finish before the next
 * open, and cart updates that do not close the popup when an add failed.
 *
 * It relies on these parts of the theme, checked once at startup. If any is
 * missing, nothing is intercepted and every link goes to the product page as
 * it always has:
 *   QuickView.prototype.quickview, show and hide; the cartUpdateUnsubscriber
 *   field; theme.pubsub; theme.a11y.trapFocus; theme.HTMLUpdateUtility.setInnerHTML.
 * It also relies on how ModalElement closes: the bubbling 'toggle' event when
 * the open attribute goes, and 'modal:afterHide' when the fade has ended and
 * QuickView.afterHide has emptied the content. MAINTAINING.md lists these for
 * a base theme upgrade.
 *
 * STATE
 * One named state on the element, in data-kc-state:
 *   idle     closed, nothing pending
 *   loading  open, the request is on its way, the loading view shows
 *   loaded   open, the design's content is in
 *   failed   open, the request failed or took longer than 15 seconds;
 *            the error view offers Try again and the full page
 *   closing  the close has started and the fade has not ended
 * aria-busy is set while loading, and the status line inside the dialog says
 * what is happening.
 *
 * CLOSE, THEN OPEN
 * The theme empties the popup and moves focus behind it when the close fade
 * ends (about 0.8 s), even if the same popup was opened again in the meantime.
 * So every open waits while the state is closing, and also while the Back
 * step of the last close is still on its way. The new design's request starts
 * at the click, so it is usually ready when the popup opens. If the close has
 * not finished within 1.5 s, the click goes to the product page instead.
 *
 * HISTORY
 * Opening pushes one entry with the same address and history.state.kcQV
 * ({ url, doc }). The address never changes, so sharing, analytics and the
 * canonical page are the grid's. Back while open closes the popup. Any other
 * close steps back over that entry when the entry below it belongs to this
 * page, so history returns to the depth it had. A reload, or a return to the
 * grid without the back/forward cache, reopens the popup from the entry and
 * stamps it with this page's id, so the next close still steps back.
 */
(function () {
  'use strict';

  if (window.KCQuickViewStarted) return;
  window.KCQuickViewStarted = true;

  if (!window.customElements || !window.fetch || !window.AbortController || !window.DOMParser ||
      !window.history || typeof window.history.pushState !== 'function') return;

  var ID = 'KcQuickView';
  var GRID = '#ProductGridContainer[data-kc-quick-view]';
  var NOT_A_DESIGN = '.kc-promo, .article-card, .product-card--promo';
  /*
   * Products the popup never opens: a click goes to the product page as a
   * plain link. Kinetic Textures is a tool, not a design, and only its product
   * page knows an owner ("You own this") or a member from a buyer, so the
   * popup must never offer it for sale again.
   */
  var PAGE_ONLY = /\/products\/kinetic-textures\/?$/;
  var LOAD_TIMEOUT = 15000;
  var CLOSE_WAIT = 1500;
  var DOC_ID = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  var KcQuickView = null;

  customElements.whenDefined('quick-view').then(start, function () {});

  function start() {
    var Base = customElements.get('quick-view');
    if (typeof Base !== 'function') return;
    if (!customElements.get('kc-quick-view')) {
      try {
        customElements.define('kc-quick-view', build(Base));
      } catch (e) {
        return;
      }
    }
    KcQuickView = customElements.get('kc-quick-view');
    if (!selfCheck(Base)) return;

    document.addEventListener('click', onClick);
    document.addEventListener('toggle', onOtherModalToggle, true);
    window.addEventListener('popstate', onPopState);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    watchGrids();
    reopenFromHistory();
  }

  function selfCheck(Base) {
    var t = window.theme;
    var p = Base.prototype;
    var node = popup();
    return !!(t && t.pubsub && typeof t.pubsub.subscribe === 'function' && t.pubsub.PUB_SUB_EVENTS &&
      t.pubsub.PUB_SUB_EVENTS.cartUpdate && t.pubsub.PUB_SUB_EVENTS.cartError &&
      t.a11y && typeof t.a11y.trapFocus === 'function' &&
      t.HTMLUpdateUtility && typeof t.HTMLUpdateUtility.setInnerHTML === 'function' &&
      typeof p.quickview === 'function' && typeof p.show === 'function' && typeof p.hide === 'function' &&
      node && node.querySelector('.quick-view__content') && 'cartUpdateUnsubscriber' in node);
  }

  /* The popup element, only once it is our upgraded element. Looked up each
     time, because the theme editor can render the section again. */
  function popup() {
    var node = document.getElementById(ID);
    return node && KcQuickView && node instanceof KcQuickView && node.kc ? node : null;
  }

  /* ---------- Which clicks open the popup ---------- */

  function designLink(a) {
    if (!a || a.target && a.target !== '_self' || a.hasAttribute('download')) return null;
    var card = a.closest('.product-card');
    if (!card || card.matches(NOT_A_DESIGN) || !card.closest(GRID)) return null;
    var u;
    try { u = new URL(a.href, location.href); } catch (e) { return null; }
    if (u.origin !== location.origin || u.pathname.indexOf('/products/') === -1) return null;
    if (PAGE_ONLY.test(u.pathname)) return null;
    return { card: card, path: u.pathname, href: u.href };
  }

  function onClick(e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (window.Shopify && window.Shopify.designMode) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    var hit = designLink(a);
    if (!hit) return;
    var qv = popup();
    if (!qv) return;
    e.preventDefault();
    try {
      qv.kcOpen(hit.path, { card: hit.card, href: hit.href, opener: titleLinkOf(hit.card) || a });
    } catch (err) {
      location.assign(hit.href);
    }
  }

  function titleLinkOf(card) {
    return card ? card.querySelector('a.product-card__title') : null;
  }

  function cardFor(path) {
    var links = document.querySelectorAll(GRID + ' .product-card a.product-card__title[href]');
    for (var i = 0; i < links.length; i++) {
      try {
        if (new URL(links[i].href, location.href).pathname === path) return links[i].closest('.product-card');
      } catch (e) { /* skip */ }
    }
    return null;
  }

  /* Links that open the popup say so to screen readers, and the full page is
     not prefetched on hover (instant-page skips data-no-instant). The grid
     gains cards through endless scroll, filters and the in-collection search,
     so one observer per grid keeps the new links marked. */
  function annotate(root) {
    if (window.Shopify && window.Shopify.designMode) return;
    var links = root.querySelectorAll('.product-card a[href]');
    for (var i = 0; i < links.length; i++) {
      if (!designLink(links[i])) continue;
      links[i].setAttribute('aria-haspopup', 'dialog');
      links[i].setAttribute('data-no-instant', '');
    }
  }

  function watchGrids() {
    var grids = document.querySelectorAll(GRID);
    for (var i = 0; i < grids.length; i++) watchGrid(grids[i]);
  }

  function watchGrid(grid) {
    var queued = false;
    annotate(grid);
    if (!window.MutationObserver) return;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () { queued = false; annotate(grid); });
    }).observe(grid, { childList: true, subtree: true });
  }

  /* What the clicked card already shows, for the loading and error views. */
  function cardInfo(card) {
    if (!card) return null;
    var info = { title: '', img: null, bg: '', credits: '', price: '', members: false, token: null, badges: [] };
    var t = titleLinkOf(card);
    if (t) {
      var copy = t.cloneNode(true);
      var hidden = copy.querySelectorAll('.sr-only');
      for (var i = 0; i < hidden.length; i++) hidden[i].remove();
      info.title = copy.textContent.trim();
    }
    var media = card.querySelector('.product-card__media');
    if (media && media.style.backgroundImage) info.bg = media.style.backgroundImage;
    var img = card.querySelector('.product-card__media img');
    if (img) info.img = { src: img.currentSrc || img.src, srcset: img.getAttribute('srcset') || '', sizes: img.getAttribute('sizes') || '' };
    info.members = !!card.querySelector('.product-card__footer-members');
    var c = card.querySelector('.product-card__footer-members-cost > span, .product-card__footer-credits > span');
    if (c) info.credits = c.textContent.trim();
    var token = card.querySelector('.product-card__footer svg');
    if (token) info.token = token.cloneNode(true);
    var p = card.querySelector('.product-card__footer-price .price__regular');
    if (p) info.price = p.textContent.trim();
    info.badges = Array.prototype.slice.call(card.querySelectorAll('.product-card__footer-badges .product-card__format'));
    return info;
  }

  /* ---------- History ---------- */

  function stateWithout(st) {
    var next = {};
    if (st && typeof st === 'object') {
      for (var k in st) if (Object.prototype.hasOwnProperty.call(st, k) && k !== 'kcQV') next[k] = st[k];
    }
    return next;
  }

  function stateWith(st, path) {
    var next = stateWithout(st);
    next.kcQV = { url: path, doc: DOC_ID };
    return next;
  }

  /* Is the entry below the current one this page's grid entry? The Navigation
     API says so directly. Without it, the doc stamp in the popup entry says the
     entry was made, or re-stamped after a reload, by this page. */
  function previousEntryIsOurs() {
    var nav = window.navigation;
    try {
      if (nav && nav.currentEntry && typeof nav.entries === 'function') {
        var i = nav.currentEntry.index;
        var list = nav.entries();
        return i > 0 && !!list[i - 1] && list[i - 1].sameDocument === true;
      }
    } catch (e) { /* fall through */ }
    var st = window.history.state;
    return !!(st && st.kcQV && st.kcQV.doc === DOC_ID);
  }

  function validPath(path) {
    return typeof path === 'string' && path.charAt(0) === '/' && path.charAt(1) !== '/' && path.indexOf('/products/') !== -1;
  }

  function onPopState(e) {
    var qv = popup();
    if (!qv) return;
    var mine = e.state && e.state.kcQV;
    if (qv.kc.pendingPop) {
      // The Back step our own close asked for.
      qv.kc.pendingPop = false;
      clearTimeout(qv.kc.popTimer);
      qv.kcWake();
      return;
    }
    if (qv.open) {
      if (!mine) {
        // Back, the Android back gesture or the iOS edge swipe: one press, one close.
        qv.kc.fromPop = true;
        qv.hide();
      } else if (validPath(mine.url) && mine.url !== qv.getAttribute('data-product-url')) {
        qv.kcOpen(mine.url, { fromHistory: true, card: cardFor(mine.url) });
      }
      return;
    }
    if (mine && validPath(mine.url)) {
      // Forward into a popup entry.
      var card = cardFor(mine.url);
      qv.kcOpen(mine.url, { fromHistory: true, card: card, opener: titleLinkOf(card) });
    }
  }

  function reopenFromHistory() {
    var st = window.history.state;
    var qv = popup();
    if (!qv || !st || !st.kcQV || window.Shopify && window.Shopify.designMode) return;
    if (!validPath(st.kcQV.url)) {
      window.history.replaceState(stateWithout(st), '');
      return;
    }
    window.history.replaceState(stateWith(st, st.kcQV.url), '');
    var card = cardFor(st.kcQV.url);
    qv.kcOpen(st.kcQV.url, { fromHistory: true, card: card, opener: titleLinkOf(card) });
  }

  function onPageHide() {
    var qv = popup();
    if (qv) qv.kcAbort();
  }

  /* INVALIDATION. The popup's purchase area shows a credit balance and an owned
     or not-owned label, read from the server when the popup loaded. A page
     restored from the back/forward cache brings that copy back exactly as it
     was, although the member may have spent credits or bought a pack on the
     page they went to. So on that restore, an open popup with a credit block,
     or one whose request was cut off by leaving, fetches its content again.
     Nothing else in the popup caches anything. */
  function onPageShow(e) {
    if (!e.persisted) return;
    var qv = popup();
    if (!qv || !qv.open) return;
    if (qv.kc.state !== 'loaded' || qv.querySelector('.quick-view__content [data-credit-download]')) qv.kcReload(false);
  }

  /* After Add to cart in the popup, the theme opens the cart drawer with the
     popup's button as the place to return focus to, and the popup then empties
     itself. Point the drawer back at the design's title link instead. */
  function onOtherModalToggle(e) {
    var qv = popup();
    var other = e.target;
    if (!qv || !other || other === qv || qv.contains(other) || !other.open || !('activeElement' in other)) return;
    var back = other.activeElement;
    if (back && back.nodeType === 1 && qv.contains(back)) other.activeElement = qv.kc.opener && qv.kc.opener.isConnected ? qv.kc.opener : null;
  }

  function otherOpenModal(self) {
    var Modal = customElements.get('modal-element');
    if (!Modal) return null;
    var list = document.querySelectorAll('[open]');
    for (var i = 0; i < list.length; i++) {
      if (list[i] !== self && list[i] instanceof Modal && !self.contains(list[i])) return list[i];
    }
    return null;
  }

  /* ---------- The views drawn while loading and on failure ---------- */

  function make(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text) n.textContent = text;
    return n;
  }

  function svgIcon(paths) {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    paths.forEach(function (d) {
      var p = document.createElementNS(ns, d.charAt(0) === 'c' ? 'circle' : 'path');
      if (d.charAt(0) === 'c') {
        var v = d.slice(1).split(',');
        p.setAttribute('cx', v[0]); p.setAttribute('cy', v[1]); p.setAttribute('r', v[2]);
      } else {
        p.setAttribute('d', d);
      }
      svg.appendChild(p);
    });
    return svg;
  }

  function arrowLink(href, label, cls) {
    var a = make('a', 'kc-qv-link' + (cls ? ' ' + cls : ''));
    a.href = href;
    a.appendChild(document.createTextNode(label));
    a.appendChild(svgIcon(['M5 12h14', 'M13 6l6 6-6 6']));
    return a;
  }

  function headerFrom(info) {
    var head = make('div', 'kc-qv-head');
    head.appendChild(make('p', 'kc-qv-head__title', info.title));
    if (info.credits || info.price || info.members) {
      var cost = make('div', 'kc-qv-cost');
      if (info.members) cost.appendChild(make('span', 'kc-qv-cost__mo', 'Members only'));
      if (info.credits) {
        var cr = make('span', 'kc-qv-cost__cr');
        if (info.token) cr.appendChild(info.token.cloneNode(true));
        var n = parseInt(info.credits, 10);
        var unit = n === 1 ? 'credit' : 'credits';
        if (info.members) {
          cr.appendChild(document.createTextNode(info.credits));
          cr.appendChild(make('span', 'sr-only', ' ' + unit));
        } else {
          cr.appendChild(document.createTextNode(info.credits + ' ' + unit));
        }
        cost.appendChild(cr);
      }
      if (!info.members && info.price) {
        if (info.credits) {
          var sl = make('span', 'kc-qv-cost__sl', '/');
          sl.setAttribute('aria-hidden', 'true');
          cost.appendChild(sl);
        }
        cost.appendChild(make('span', 'kc-qv-cost__pr', info.price));
      }
      head.appendChild(cost);
    }
    if (info.badges.length) {
      var row = make('div', 'kc-qv-badges');
      info.badges.forEach(function (b) {
        var c = b.cloneNode(true);
        c.className = 'kc-qv-badge';
        row.appendChild(c);
      });
      head.appendChild(row);
    }
    return head;
  }

  /* kind: 'loading', 'failed' or 'gone'. */
  function stateView(kind, info, href) {
    info = info || { title: '', img: null, bg: '', credits: '', price: '', members: false, token: null, badges: [] };
    var view = make('div', 'kc-qv-view');
    view.setAttribute('data-kc-qv-view', kind);

    // The picture on the card's own background (Theme settings > Product
    // image background), as the gallery will show it.
    var media = make('div', 'kc-qv-view__media');
    if (info.bg) media.style.backgroundImage = info.bg;
    if (info.img && info.img.src) {
      var img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      if (info.img.srcset) img.srcset = info.img.srcset;
      if (info.img.sizes) img.sizes = info.img.sizes;
      img.src = info.img.src;
      media.appendChild(img);
    }
    view.appendChild(media);

    var body = make('div', 'kc-qv-view__info');
    if (info.title) body.appendChild(headerFrom(info));

    if (kind === 'loading') {
      var skel = make('div', 'kc-qv-skel');
      skel.setAttribute('aria-hidden', 'true');
      skel.appendChild(make('i', 'kc-qv-skel__line'));
      skel.appendChild(make('i', 'kc-qv-skel__button'));
      skel.appendChild(make('i', 'kc-qv-skel__line kc-qv-skel__line--long'));
      body.appendChild(skel);
      view.appendChild(body);
      var more = make('div', 'kc-qv-view__more');
      more.appendChild(arrowLink(href, 'View full details'));
      view.appendChild(more);
      return view;
    }

    var err = make('div', 'kc-qv-err');
    err.setAttribute('role', 'alert');
    err.appendChild(svgIcon(['c12,12,9', 'M12 7v6', 'M12 17h.01']));
    var words = make('div', 'kc-qv-err__words');
    if (kind === 'gone') {
      words.appendChild(make('p', 'kc-qv-err__what', 'This design is no longer available.'));
      words.appendChild(make('p', 'kc-qv-err__do', 'Close this to keep browsing.'));
    } else {
      words.appendChild(make('p', 'kc-qv-err__what', 'This design did not load.'));
      words.appendChild(make('p', 'kc-qv-err__do', 'Try again, or open its full page.'));
    }
    err.appendChild(words);
    body.appendChild(err);
    if (kind !== 'gone') {
      var actions = make('div', 'kc-qv-err__actions');
      var retry = make('button', 'kc-qv-retry', 'Try again');
      retry.type = 'button';
      retry.setAttribute('data-kc-qv-retry', '');
      actions.appendChild(retry);
      actions.appendChild(arrowLink(href, 'Open the full page'));
      body.appendChild(actions);
    }
    view.appendChild(body);
    return view;
  }

  /* ---------- The element ---------- */

  function build(Base) {
    return class extends Base {
      constructor() {
        super();
        this.kc = {
          state: 'idle',
          token: 0,
          opener: null,
          card: null,
          fromPop: false,
          pendingPop: false,
          popTimer: 0,
          closing: false,
          waiters: [],
          cartErrorAt: 0,
          cartErrorOff: null,
          announceTimer: 0,
          counted: false,
          defaultLabel: this.getAttribute('aria-label') || 'Design quick view'
        };
        this.kcLoad = null;
        this.addEventListener('toggle', this.kcOnToggle.bind(this));
        this.addEventListener('modal:afterHide', this.kcOnAfterHide.bind(this));
        this.addEventListener('click', this.kcOnInnerClick.bind(this));
      }

      /* Focus lands on the labelled close button when the popup opens, not on
         the unlabelled drag handle, or on Try again when the design did not
         load. The theme focuses this when its opening fade ends, which can be
         after the error view is already in. */
      get focusElement() {
        var retry = this.kc && this.kc.state === 'failed' ? this.querySelector('[data-kc-qv-retry]') : null;
        return retry || this.querySelector('.drawer__close') || super.focusElement;
      }

      /* Runs on every open and close, because the theme moves the popup to the
         end of <body> while it is open and back afterwards. Each time, the
         theme's own "hide on every cart update" is swapped for ours, and the
         cart error listener is renewed. */
      connectedCallback() {
        super.connectedCallback();
        var ps = window.theme.pubsub;
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = ps.subscribe(ps.PUB_SUB_EVENTS.cartUpdate, this.kcOnCartUpdate.bind(this));
        if (this.kc.cartErrorOff) this.kc.cartErrorOff();
        this.kc.cartErrorOff = ps.subscribe(ps.PUB_SUB_EVENTS.cartError, this.kcOnCartError.bind(this));
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        if (this.kc.cartErrorOff) {
          this.kc.cartErrorOff();
          this.kc.cartErrorOff = null;
        }
      }

      /* A failed Add to cart publishes cartError and then cartUpdate. The
         popup stays open for that one update, so the theme's error message can
         be read. Any other cart update closes it, so the cart drawer can open. */
      kcOnCartError() {
        if (this.open) this.kc.cartErrorAt = Date.now();
      }

      kcOnCartUpdate() {
        var afterError = this.kc.cartErrorAt && Date.now() - this.kc.cartErrorAt < 10000;
        this.kc.cartErrorAt = 0;
        if (afterError) return;
        this.hide();
      }

      kcSet(state, announce) {
        this.kc.state = state;
        this.setAttribute('data-kc-state', state);
        if (state === 'loading') this.setAttribute('aria-busy', 'true');
        else this.removeAttribute('aria-busy');
        if (typeof announce === 'string') this.kcAnnounce(announce);
      }

      /* Written a moment after the change, once the dialog has moved into
         place, so screen readers pick it up. */
      kcAnnounce(text) {
        var line = this.querySelector('[data-kc-qv-status]');
        if (!line) return;
        clearTimeout(this.kc.announceTimer);
        line.textContent = '';
        if (!text) return;
        this.kc.announceTimer = setTimeout(function () { line.textContent = text; }, 150);
      }

      kcAbort() {
        if (this.kcLoad && this.kcLoad.ctrl) {
          try { this.kcLoad.ctrl.abort(); } catch (e) { /* already done */ }
        }
      }

      /* One request per open, started at the click. It carries the open's
         token, so a response for an open that has since closed or been
         replaced is dropped. This controller is ours: the theme's own
         this.abortController is for its listeners, and it is aborted every
         time the popup moves into <body>. */
      kcFetch(path, token) {
        this.kcAbort();
        var ctrl = new AbortController();
        var timedOut = false;
        var timer = setTimeout(function () { timedOut = true; ctrl.abort(); }, LOAD_TIMEOUT);
        var promise = fetch(path + '?view=modal', { cache: 'no-cache', credentials: 'same-origin', signal: ctrl.signal })
          .then(function (res) {
            return res.text().then(function (text) { return { status: res.status, ok: res.ok, text: text }; });
          })
          .catch(function () {
            // Aborted by our own close or by leaving the page: drop it quietly.
            // A timeout or a network error: show the error view.
            return { status: 0, ok: false, text: '', aborted: ctrl.signal.aborted && !timedOut, timedOut: timedOut };
          })
          .then(function (r) {
            clearTimeout(timer);
            return r;
          });
        this.kcLoad = { token: token, path: path, ctrl: ctrl, promise: promise };
        return this.kcLoad;
      }

      /* Resolves true once no close is in progress and no Back step of ours is
         on its way, or false after CLOSE_WAIT. */
      kcSettled() {
        var self = this;
        if (!this.kc.closing && !this.kc.pendingPop) return Promise.resolve(true);
        return new Promise(function (resolve) {
          var done = false;
          var timer = setTimeout(function () { finish(false); }, CLOSE_WAIT);
          function finish(value) {
            if (done) return true;
            done = true;
            clearTimeout(timer);
            resolve(value);
            return true;
          }
          self.kc.waiters.push(function () {
            if (done) return true;
            if (self.kc.closing || self.kc.pendingPop) return false;
            return finish(true);
          });
        });
      }

      /* Run after the current task, so the theme has finished its own close
         (it sets hidden right after the afterHide event). */
      kcWake() {
        var self = this;
        setTimeout(function () {
          self.kc.waiters = self.kc.waiters.filter(function (w) { return !w(); });
        }, 0);
      }

      kcOpen(path, opts) {
        opts = opts || {};
        var self = this;
        var token = ++this.kc.token;
        this.kcFetch(path, token);
        var card = opts.card || null;
        return this.kcSettled().then(function (ready) {
          if (token !== self.kc.token) return;
          if (!ready) {
            self.kcAbort();
            location.assign(opts.href || path);
            return;
          }
          self.kc.card = cardInfo(card);
          self.kc.counted = false;
          self.setAttribute('aria-label', (self.kc.card && self.kc.card.title) || self.kcDefaultLabel());
          if (self.open) {
            // Already open on another design: swap the content in place.
            self.setAttribute('data-product-url', path);
            if (!opts.fromHistory) window.history.replaceState(stateWith(window.history.state, path), '');
            self.quickview();
            return;
          }
          self.kc.opener = opts.opener || null;
          self.setAttribute('data-product-url', path);
          if (!opts.fromHistory) {
            try { window.history.pushState(stateWith(window.history.state, path), ''); } catch (e) { /* no entry, Back leaves the grid as before */ }
          }
          try {
            self.show(self.kc.opener);
          } catch (err) {
            self.kcAbort();
            location.assign(opts.href || path);
          }
        });
      }

      kcDefaultLabel() {
        return this.kc.defaultLabel;
      }

      /* Fetch the same design again, for Try again and the back/forward cache
         restore. Does not count another view. */
      kcReload(announce) {
        if (!this.open) return;
        var path = this.getAttribute('data-product-url');
        if (!validPath(path)) return;
        this.kc.token++;
        this.kcFetch(path, this.kc.token);
        this.quickview(announce);
      }

      /* Replaces the theme's quickview(), which runs from beforeShow on every
         open: draw the loading view at once (never the last design), reuse the
         request the click started, then fill or fail. */
      quickview(announce) {
        var content = this.querySelector('.quick-view__content');
        var path = this.getAttribute('data-product-url');
        if (!content || !validPath(path)) return;
        var token = this.kc.token;
        var req = this.kcLoad && this.kcLoad.token === token ? this.kcLoad : this.kcFetch(path, token);
        content.textContent = '';
        content.appendChild(stateView('loading', this.kc.card, path));
        var title = (this.kc.card && this.kc.card.title) || 'the design';
        this.kcSet('loading', announce === false ? undefined : 'Loading ' + title);
        content.scrollTop = 0;
        var self = this;
        req.promise.then(function (r) { self.kcFill(content, path, token, r); });
      }

      kcFill(content, path, token, r) {
        if (token !== this.kc.token || !this.open || r.aborted) return;
        var html = null;
        if (r.ok && r.text) {
          var doc = new DOMParser().parseFromString(r.text, 'text/html');
          var node = doc.querySelector('.quick-view__content');
          if (node && node.children.length) html = node.innerHTML;
        }
        if (html === null) {
          this.kcFail(content, path, r.status === 404 ? 'gone' : 'failed');
          return;
        }
        window.theme.HTMLUpdateUtility.setInnerHTML(content, html);
        this.kcSet('loaded', '');
        var heading = content.querySelector('[data-kc-quick-header] h2');
        if (heading && heading.textContent.trim()) this.setAttribute('aria-label', heading.textContent.trim());
        var keep = document.activeElement && document.activeElement !== document.body && this.contains(document.activeElement);
        try { window.theme.a11y.trapFocus(this, keep ? document.activeElement : this.focusElement); } catch (e) { /* focus stays */ }
        if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
          try { window.Shopify.PaymentButton.init(); } catch (e) { /* express checkout stays hidden */ }
        }
        document.dispatchEvent(new CustomEvent('quickview:loaded', { detail: { productUrl: path } }));
        this.kcCountView(content);
      }

      /* A popup open counts as a view, as a product page load does
         (snippets/kc-view-count.liquid, which the shell renders to define
         KCView). Once per open: Try again, or the refresh after the
         back/forward cache restores the page, does not count again. */
      kcCountView(content) {
        if (this.kc.counted || typeof window.KCView !== 'function') return;
        var el = content.querySelector('[data-kc-product-id]');
        if (!el) return;
        this.kc.counted = true;
        try { window.KCView(el.getAttribute('data-kc-product-id')); } catch (e) { /* never blocks the popup */ }
      }

      kcFail(content, path, kind) {
        content.textContent = '';
        content.appendChild(stateView(kind, this.kc.card, path));
        this.kcSet('failed', '');
        var retry = content.querySelector('[data-kc-qv-retry]');
        try { window.theme.a11y.trapFocus(this, retry || this.focusElement); } catch (e) { /* focus stays */ }
      }

      kcOnInnerClick(e) {
        var retry = e.target && e.target.closest ? e.target.closest('[data-kc-qv-retry]') : null;
        if (!retry || !this.contains(retry)) return;
        e.preventDefault();
        this.kcReload();
      }

      kcOnToggle(e) {
        if (e.target !== this || this.open) return;
        // A close has started: the close button, Escape, the dimmed page, a
        // swipe, Back, or the theme closing it for a cart update.
        this.kc.token++;
        this.kcAbort();
        this.kc.closing = true;
        this.kcSet('closing', '');
        if (this.kc.fromPop) {
          this.kc.fromPop = false;
          return;
        }
        var st = window.history.state;
        if (!st || !st.kcQV) return;
        if (previousEntryIsOurs()) {
          var self = this;
          this.kc.pendingPop = true;
          clearTimeout(this.kc.popTimer);
          this.kc.popTimer = setTimeout(function () {
            self.kc.pendingPop = false;
            self.kcWake();
          }, CLOSE_WAIT);
          window.history.back();
        } else {
          window.history.replaceState(stateWithout(st), '');
        }
      }

      kcOnAfterHide(e) {
        if (e.target !== this) return;
        this.kc.closing = false;
        this.kc.card = null;
        this.kcSet('idle', '');
        this.kcWake();
      }

      /* The theme returns focus to the opener when the fade ends. When another
         modal opened meanwhile (the cart drawer after Add to cart), focus stays
         in that modal instead of moving behind it. */
      afterHide() {
        var other = otherOpenModal(this);
        if (other) this.activeElement = null;
        super.afterHide();
        if (!other) return;
        setTimeout(function () {
          if (!other.open) return;
          var inside = document.activeElement && other.contains(document.activeElement) ? document.activeElement : other.focusElement || other;
          try { window.theme.a11y.trapFocus(other, inside); } catch (e) { /* focus stays */ }
        }, 0);
      }
    };
  }
})();
